<script setup>
import EvalTable from '@/components/table/Table.vue';
import { onMounted, onUnmounted, ref, computed } from 'vue';
import axios from '@/plugins/axios';
import BaseCardTable from '@/components/table/BaseCardTable.vue';
import Modal from '@/components/modals/Modal.vue';
import RunModelBenchmarkModal from '@/components/modals/RunModelBenchmarkModal.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import { downloadEvalReport, fetchDatasets } from '@/services/api';
import { useNotifications } from '@/composables/use-notifications';
import { useRunWithModels } from '@/composables/useRunWithModels';

const { onNewNotificationReceived, offNewNotificationReceived } = useNotifications();
const { isOpening: isOpeningRunModal, openWithFeedback } = useRunWithModels();
let modelRunNotificationHandler = null;

const loading = ref(false);
const isSaleModalOpen = ref(false);
const saleSelected = ref(null);
const isRunModalOpen = ref(false);
const runDatasetId = ref(null);
const runDatasetName = ref('');
const ownedDatasets = ref([]);

const runs = ref([]);
const rowData = ref([]);
const cardData = ref([]);
const quickFilterText = ref('');
const currentPage = ref(1);
const totalItems = computed(() => filteredData.value.length);
const itemsPerPage = ref(10);
const sortBy = ref('issue_date');
const sortOrder = ref('desc');

// Paginação para a tabela de resultados do modal
const evalResultCurrentPage = ref(1);
const evalResultItemsPerPage = ref(10);

const isLoading = ref(false);

const columns = [
    {
        key: 'evaluation',
        label: 'EVALUATION',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    {
        key: 'seller',
        label: 'SELLER',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    {
        key: 'issue_date',
        label: 'ISSUE DATE',
        type: 'date',
        sortable: true,
        align: 'left'
    },
    {
        key: 'kind',
        label: 'KIND',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    {
        key: 'queue_position',
        label: 'QUEUE',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    {
        key: 'status',
        label: 'STATUS',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    {
        key: 'download',
        label: 'DOWNLOAD REPORT',
        type: 'text',
        sortable: false,
        align: 'center'
    },
];

const evalResultColumns = [
    {
        key: 'task_id',
        label: 'TASK ID',
        type: 'text',
        sortable: false,
        align: 'left'
    },
    {
        key: 'verdict',
        label: 'VERDICT',
        type: 'text',
        sortable: false,
        align: 'left'
    },
]

const fetchRuns = async () => {
    const { data } = await axios.get('/api/eval-runs');
    let items;
    if (Array.isArray(data?.evalRuns)) {
        items = data.evalRuns;
    } else if (Array.isArray(data)) {
        items = data;
    } else {
        items = data?.data || [];
    }
    runs.value = items;

    rowData.value = runs.value.map((run) => {
        return {
            id: run.id,
            evaluation: run?.sale?.listing?.name ?? (run?.run_kind === 'model_run' ? `Model run #${run?.dataset_id ?? ''}` : '—'),
            seller: run?.sale?.listing?.datasets?.[0]?.seller?.user?.username || (run?.run_kind === 'model_run' ? 'Self-serve' : '—'),
            eval_price: run?.sale?.listing?.eval_price || '—',
            issue_date: run?.created_at,
            status: run?.status,
            kind: run?.run_kind === 'model_run' ? 'model_run' : 'upload',
            queue_position: run?.queue_position ?? null,
            model_response_file: run?.model_response_file,
            sale_id: run?.sale_id,
            dataset_id: run?.dataset_id,
            cost: run?.cost,
            token_cnt: run?.token_cnt,
        };
    });

    cardData.value = rowData.value.map((item) => ({
        id: item.id,
        title: item.evaluation,
        eval_price: item.eval_price,
        rows: [
            { column: 'Seller', type: 'text', value: item.seller },
            { column: 'Issue Date', type: 'date', value: item.issue_date },
            {
                column: 'Queue',
                type: 'text',
                value:
                    item.kind === 'model_run' &&
                    item.queue_position != null &&
                    String(item.status).toLowerCase() !== 'finish' &&
                    String(item.status).toLowerCase() !== 'failed'
                        ? `#${item.queue_position}`
                        : '—',
            },
            { column: 'Status', type: 'tag', value: getBadgeConfig(item).text, color: getBadgeConfig(item).type },
        ],
    }));
};

const handleSort = (column) => {
    if (sortBy.value === column) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortOrder.value = 'asc';
    }
};

const sortedData = computed(() => {
    if (!sortBy.value) return filteredData.value;
    return [...filteredData.value].sort((a, b) => {
        let aValue = a[sortBy.value];
        let bValue = b[sortBy.value];
        if (sortBy.value === 'issue_date') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
        }
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const cmp = aValue.localeCompare(bValue);
            return sortOrder.value === 'asc' ? cmp : -cmp;
        }
        if (aValue < bValue) return sortOrder.value === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.value === 'asc' ? 1 : -1;
        return 0;
    });
});

const evalResultSortedData = computed(() => {
    if (!evalResultContent.value?.report?.results) return [];
    return evalResultContent.value.report.results.map((result) => ({
        task_id: result.task_id || '—',
        verdict: result.verdict || '—',
        passed_count: result.passed_count ?? 0,
        total_criteria: result.total_criteria ?? 0,
        flagged: result.flagged_for_human || false,
        per_criterion: result.per_criterion || [],
        rubric: result.rubric || {}
    }));
})

const getBadgeConfig = (item) => {
    const status = String(item.status || '').toLowerCase();
    switch (status) {
        case 'pending':
            return { text: 'Pending', type: 'warning' };
        case 'running':
            return { text: 'Running', type: 'warning' };
        case 'finish':
            return { text: 'Completed', type: 'success' };
        case 'failed':
            return { text: 'Failed', type: 'default' };
        default:
            return { text: item.status || '—', type: 'default' };
    }
};

const openRunModal = async () => {
    await openWithFeedback({
        errorFallback: 'Failed to load evals for model run. Please try again.',
        action: async ({ addToast, ensureCatalogAvailable }) => {
            const datasets = await fetchDatasets(0, 50, 'eval', undefined, true);
            ownedDatasets.value = Array.isArray(datasets) ? datasets : [];
            if (!ownedDatasets.value.length) {
                addToast('No evals available to run a model benchmark.', 'error');
                return false;
            }
            const catalogReady = await ensureCatalogAvailable();
            if (!catalogReady) {
                return false;
            }
            const first = ownedDatasets.value[0];
            runDatasetId.value = first.id;
            runDatasetName.value = first.name || '';
            isRunModalOpen.value = true;
            return true;
        },
    });
};

const onDatasetPick = (event) => {
    const id = Number(event.target.value);
    runDatasetId.value = id;
    const found = ownedDatasets.value.find((d) => Number(d.id) === id);
    runDatasetName.value = found?.name || '';
};

const filteredData = computed(() => {
    if (!quickFilterText.value) return rowData.value;
    const searchTerm = quickFilterText.value.toLowerCase();
    return rowData.value.filter((item) =>
        Object.values(item).some((value) => String(value ?? '').toLowerCase().includes(searchTerm))
    );
});

const handleDownload = async (item) => {
    try {
        const fileResponse = await downloadEvalReport(item.id, 'file', item.evaluation);
        if (fileResponse && fileResponse.blob) {
            const url = URL.createObjectURL(fileResponse.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileResponse.filename || 'eval_report.json';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            return;
        }
    } catch (error) {
        console.error('Error downloading eval report:', error);
    }
};

const evalResultContent = ref(null);
const isEvalResultModalOpen = ref(false);
const handleSeeResult = async (item) => {
    try {
        isLoading.value = true;
        await downloadEvalReport(item.id).then((response) => {
            evalResultContent.value = response;
            evalResultCurrentPage.value = 1;
            isEvalResultModalOpen.value = true;
        }).finally(() => {
            isLoading.value = false;
        })
    } catch (error) {
        console.error('Error seeing eval result:', error);
    }
};

const handleViewSale = (saleId) => {
    saleSelected.value = runs.value.find((run) => run.sale_id === saleId);
    isSaleModalOpen.value = !!saleSelected.value;
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

onMounted(async () => {
    loading.value = true;
    try {
        await fetchRuns();
    } finally {
        loading.value = false;
    }

    modelRunNotificationHandler = (notification) => {
        if (
            notification?.type === 'model_run.queue' ||
            notification?.type === 'model_run.report'
        ) {
            fetchRuns();
        }
    };
    onNewNotificationReceived(modelRunNotificationHandler);
});

onUnmounted(() => {
    if (modelRunNotificationHandler) {
        offNewNotificationReceived(modelRunNotificationHandler);
    }
});
</script>

<template>
    <div>
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 class="text-lg font-semibold text-white">Eval Results</h2>
            <div class="flex flex-wrap items-center gap-2">
                <select
                    v-if="isRunModalOpen || ownedDatasets.length"
                    class="bg-echoes-background-black border border-echoes-border-light rounded-lg px-2 py-1 text-sm text-white"
                    :value="runDatasetId || ''"
                    @change="onDatasetPick"
                >
                    <option v-for="ds in ownedDatasets" :key="ds.id" :value="ds.id">{{ ds.name }}</option>
                </select>
                <button
                    type="button"
                    class="inline-flex items-center px-3 py-1.5 rounded-lg bg-echoes-primary text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="eval-results-run-with-models"
                    :disabled="isOpeningRunModal"
                    @click="openRunModal"
                >
                    <i
                        v-if="isOpeningRunModal"
                        class="mdi mdi-loading mdi-spin mr-1 text-base"
                    ></i>
                    {{ isOpeningRunModal ? 'Loading...' : 'Run with models' }}
                </button>
            </div>
        </div>

        <RunModelBenchmarkModal
            :isOpen="isRunModalOpen"
            :dataset-id="runDatasetId"
            :dataset-name="runDatasetName"
            @close="isRunModalOpen = false"
            @created="fetchRuns"
        />

        <Modal :isOpen="isSaleModalOpen" title="Transaction Receipt" size="lg" @close="isSaleModalOpen = false">
            <!-- Transaction Details -->
            <div class="receipt-content">
                <div class="section">
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="label">Transaction ID</span>
                            <span class="value">{{ saleSelected?.sale.tx_hash || 'N/A' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">Date</span>
                            <span class="value">{{ formatDate(saleSelected?.created_at) }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">Payment Method</span>
                            <span class="value">{{ saleSelected?.sale.payment_method?.toUpperCase() || 'N/A' }}</span>
                        </div>
                    </div>
                </div>

                <div class="section">
                    <h4 class="section-title">Listing Details</h4>
                    <div class="purchase-item">
                        <div class="item-info">
                            <h5 class="item-name">{{ saleSelected?.sale.listing.name }}</h5>
                            <p class="item-description">{{ saleSelected?.sale.listing.short_description }}</p>
                        </div>
                    </div>
                </div>

                <!-- Total Section -->
                <div class="total-section">
                    <div class="total-line">
                        <span class="total-label">Total Amount</span>
                        <span class="total-value">{{ saleSelected?.sale.final_price || '0' }} {{
                            saleSelected?.sale.currency || 'USD' }}</span>
                    </div>
                </div>
            </div>
        </Modal>
        <Modal :isOpen="isEvalResultModalOpen" title="Eval Result" size="4xl" @close="isEvalResultModalOpen = false">
            <div class="p-3 bg-echoes-background-black rounded-xl">
                <div>
                    <div v-if="isLoading" class="flex items-center justify-center py-12">
                        <div class="flex flex-col items-center gap-3">
                            <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                        </div>
                    </div>

                <div v-else class="flex flex-col gap-3">
                    <div class="grid grid-cols-3 gap-3">
                        <StatCard>
                            <template #title>Task</template>
                            <template #value>{{ evalResultContent?.report?.total_tasks || 0 }}</template>
                        </StatCard>

                        <StatCard>
                            <template #title>Pass</template>
                            <template #value>{{ evalResultContent?.report?.pass_count || 0 }}</template>
                        </StatCard>

                        <StatCard>
                            <template #title>Fail</template>
                            <template #value>{{ evalResultContent?.report?.fails_count || 0 }}</template>
                        </StatCard>
                    </div>
               
                <div>

                    <EvalTable :data="evalResultSortedData" :columns="evalResultColumns"
                        :totalItems="evalResultSortedData.length" :currentPage="evalResultCurrentPage"
                        @update:currentPage="evalResultCurrentPage = $event" :itemsPerPage="evalResultItemsPerPage"
                        @update:itemsPerPage="evalResultItemsPerPage = $event" :show-search="false"
                        :show-items-per-page="false" emptyMessage="No eval results found" class="hidden md:block">
                        <template #column-task_id="{ item }">
                            <div class="cursor-default font-mono text-sm truncate max-w-[200px]">
                                {{ item.task_id }}
                            </div>
                        </template>

                        <template #column-verdict="{ item }">
                            <span :class="[
                                'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                                item.verdict === 'pass' ? 'bg-green-500/20 text-green-400 border border-green-500' :
                                    item.verdict === 'fail' ? 'bg-red-500/20 text-red-400 border border-red-500' :
                                        item.verdict === 'flagged' ? 'bg-orange-500/20 text-orange-400 border border-orange-500' :
                                            'bg-gray-500/20 text-gray-400 border border-gray-500'
                            ]">
                                {{ item.verdict.toUpperCase() }}
                            </span>
                        </template>
                    </EvalTable>
                    </div>
                    </div>
                    
            </div>
            </div>
         </Modal>
        <BaseCardTable :items="cardData" :quickFilterText="quickFilterText" :isLoading="loading" class="md:hidden" />
        <!-- eslint-disable-next-line vuejs-accessibility/require-table-header -->
        <EvalTable :data="sortedData" :columns="columns" :totalItems="totalItems" :searchQuery="quickFilterText"
            @update:searchQuery="quickFilterText = $event" :currentPage="currentPage"
            @update:currentPage="currentPage = $event" :itemsPerPage="itemsPerPage"
            @update:itemsPerPage="itemsPerPage = $event" :sortBy="sortBy" :sortOrder="sortOrder" @sort="handleSort"
            title="Eval Runs" searchPlaceholder="Search eval runs..." emptyMessage="No eval runs found"
            class="hidden md:block">
            
            <template #column-evaluation="{ item }">
                <div class="cursor-default font-semibold w-fit truncate max-w-[260px]">
                    {{ item.evaluation }}
                </div>
            </template>

            <template #column-status="{ item }">
                <span :class="[
                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                    getBadgeConfig(item).type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500' :
                        getBadgeConfig(item).type === 'warning' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500' :
                            'bg-gray-500/20 text-gray-400 border border-gray-500'
                ]">
                    {{ getBadgeConfig(item).text }}
                </span>
            </template>

            <template #column-kind="{ item }">
                <span
                    :class="[
                        'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                        item.kind === 'model_run'
                            ? 'bg-sky-500/20 text-sky-300 border border-sky-500'
                            : 'bg-white/10 text-white/70 border border-white/20'
                    ]"
                >
                    {{ item.kind === 'model_run' ? 'Model run' : 'Upload' }}
                </span>
            </template>

            <template #column-queue_position="{ item }">
                <span
                    v-if="
                        item.kind === 'model_run' &&
                        item.queue_position != null &&
                        String(item.status).toLowerCase() !== 'finish' &&
                        String(item.status).toLowerCase() !== 'failed'
                    "
                    class="text-sm text-amber-200"
                >
                    #{{ item.queue_position }}
                </span>
                <span v-else class="text-sm text-white/40">—</span>
            </template>

            <template #column-download="{ item }">
                <div class="flex items-center justify-center gap-2">
                    <div v-if="String(item.status).toLowerCase() !== 'finish'">
                        <div class="flex items-center gap-2 text-xs text-white/70">
                            <div class="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white/70"></div>
                            <span>Download will be available soon</span>
                        </div>
                    </div>
                    <div v-else class="flex items-center gap-2">
                        <button @click="handleDownload(item)" :disabled="!item.model_response_file" :class="[
                            'flex items-center gap-2 px-3 py-1 rounded-md transition-colors duration-200',
                            item.model_response_file ? 'bg-orange-600 hover:bg-orange-600/80 text-white' : 'bg-gray-600/40 text-white/60 cursor-not-allowed'
                        ]">
                            <i class="uil uil-import"></i>
                            Download
                        </button>
                        <button @click="handleSeeResult(item)" :disabled="!item.model_response_file" :class="[
                            'flex items-center gap-2 px-3 py-1 rounded-md transition-colors duration-200',
                            item.model_response_file ? 'bg-orange-600 hover:bg-orange-600/80 text-white' : 'bg-gray-600/40 text-white/60 cursor-not-allowed'
                        ]">
                            <i class="uil uil-eye"></i>
                        </button>
                    </div>
                    <button v-if="item.sale_id" class="view-sale-button" @click="handleViewSale(item.sale_id)">
                        <i class="uil uil-receipt-alt text-lg"></i>
                        <span class="tooltip">View Transaction Receipt</span>
                    </button>
                </div>
            </template>
        </EvalTable>
    </div>
</template>

<style scoped lang="postcss">
.view-sale-button {
    @apply text-white/80 hover:text-echoes-primary transition-colors relative bg-echoes-background-gray rounded-md p-0.5 px-1;
    position: relative;
    text-decoration: none;
}

.tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs text-gray-400 bg-echoes-background-gray rounded-lg border border-gray-500 whitespace-normal w-max max-w-[150px];
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 50;
}

.tooltip::after {
    content: '';
    @apply absolute left-1/2 -translate-x-1/2 -bottom-1 border-4 border-transparent border-t-gray-900;
}

.view-sale-button:hover .tooltip {
    visibility: visible;
    opacity: 1;
}

/* Receipt Styles */

.receipt-content {
    @apply p-2 space-y-6;
}

.section {
    @apply space-y-4;
}

.section-title {
    @apply text-lg font-semibold text-white border-b border-echoes-border-light pb-2;
}

.info-grid {
    @apply grid grid-cols-1 gap-3;
}

.info-item {
    @apply flex justify-between items-center py-2;
}

.info-item .label {
    @apply text-sm text-white/70 font-medium;
}

.info-item .value {
    @apply text-sm text-white font-semibold truncate max-w-[200px];
}

.purchase-item {
    @apply bg-echoes-background-black/50 rounded-lg p-4 border border-echoes-border-light/50;
}

.item-info {
    @apply flex-1;
}

.item-name {
    @apply text-base font-semibold text-white mb-1;
}

.item-description {
    @apply text-sm text-white/70 line-clamp-2;
}

.total-section {
    @apply bg-echoes-background-black/30 rounded-lg p-4 border border-echoes-border-light/30;
}

.total-line {
    @apply flex justify-between items-center py-2;
}

.total-label {
    @apply text-base font-semibold text-white;
}

.total-value {
    @apply text-xl font-bold text-echoes-primary;
}

.eval-result-content {
    @apply p-4 space-y-4;
}

.eval-result-content pre {
    @apply bg-echoes-background-black/50 rounded-lg p-4 border border-echoes-border-light/50 text-sm max-h-[70vh] overflow-y-auto;
}
</style>